/**
 * Tiny IndexedDB-backed outbox for messages sent while offline.
 *
 * Why no library: the API surface we need is two-method (put/list/delete +
 * count). Keeping it dependency-free is ~50 lines of stable code vs. an
 * external dep that ships polyfills for browsers we don't support anyway.
 *
 * The store schema:
 *   db: 'orenburg56-outbox' v1
 *   stores:
 *     - 'pending': { keyPath: 'clientMessageId' }
 */

export interface OutboxItem {
  clientMessageId: string;
  channelId?: string;
  recipientId?: string;
  content: string;
  type?: 'TEXT' | 'IMAGE' | 'VOICE' | 'FILE';
  mediaFileId?: string;
  replyToId?: string;
  createdAt: number;
  retryCount: number;
}

const DB_NAME = 'orenburg56-outbox';
const DB_VERSION = 1;
const STORE = 'pending';

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'clientMessageId' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function tx(mode: IDBTransactionMode): Promise<IDBObjectStore> {
  return openDb().then((db) => db.transaction(STORE, mode).objectStore(STORE));
}

export const outbox = {
  async put(item: OutboxItem): Promise<void> {
    const store = await tx('readwrite');
    return new Promise((resolve, reject) => {
      const r = store.put(item);
      r.onsuccess = () => resolve();
      r.onerror = () => reject(r.error);
    });
  },

  async list(): Promise<OutboxItem[]> {
    const store = await tx('readonly');
    return new Promise((resolve, reject) => {
      const r = store.getAll();
      r.onsuccess = () => resolve((r.result || []) as OutboxItem[]);
      r.onerror = () => reject(r.error);
    });
  },

  async delete(clientMessageId: string): Promise<void> {
    const store = await tx('readwrite');
    return new Promise((resolve, reject) => {
      const r = store.delete(clientMessageId);
      r.onsuccess = () => resolve();
      r.onerror = () => reject(r.error);
    });
  },

  async count(): Promise<number> {
    const store = await tx('readonly');
    return new Promise((resolve, reject) => {
      const r = store.count();
      r.onsuccess = () => resolve(r.result);
      r.onerror = () => reject(r.error);
    });
  },

  async incrementRetry(clientMessageId: string): Promise<void> {
    const all = await this.list();
    const item = all.find((i) => i.clientMessageId === clientMessageId);
    if (!item) return;
    item.retryCount += 1;
    if (item.retryCount > 5) await this.delete(clientMessageId);
    else await this.put(item);
  },
};

/** Generate a v4-ish UUID for clientMessageId. */
export function newClientMessageId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  // RFC4122-ish fallback
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
